/**
 * useCapabilityQuestions - Hook for fetching and managing capability questions
 *
 * Provides capability questions for product-agnostic maturity assessment,
 * handles answer persistence, and derives maturity levels from answers.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useCallback } from 'react';
import apiClient from '../../../lib/apiClient';
import { MATURITY_LEVELS } from '../../../config/zeroTrustConstants';

/**
 * Fetch all capability questions, optionally filtered by function
 */
export function useCapabilityQuestions(functionCode = null) {
  return useQuery({
    queryKey: ['cisa', 'capability-questions', functionCode],
    queryFn: async () => {
      const path = functionCode
        ? `/api/cisa/capability-questions/${functionCode}`
        : '/api/cisa/capability-questions';
      return apiClient.get(path);
    },
    staleTime: 5 * 60 * 1000, // Questions rarely change
  });
}

/**
 * Fetch capability answers for a department
 */
export function useCapabilityAnswers(departmentId, functionCode = null) {
  return useQuery({
    queryKey: ['cisa', 'capability-answers', departmentId, functionCode],
    queryFn: async () => {
      const params = functionCode ? `?function=${functionCode}` : '';
      return apiClient.get(`/api/cisa/departments/${departmentId}/capability-answers${params}`);
    },
    enabled: !!departmentId,
  });
}

/**
 * Fetch derived maturity levels from capability answers
 */
export function useDerivedMaturityFromCapabilities(departmentId) {
  return useQuery({
    queryKey: ['cisa', 'derived-maturity-capabilities', departmentId],
    queryFn: async () => {
      return apiClient.get(`/api/cisa/departments/${departmentId}/derived-maturity-from-capabilities`);
    },
    enabled: !!departmentId,
  });
}

/**
 * Mutation to save capability answers
 */
export function useSaveCapabilityAnswers(departmentId) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ answers, workshopSessionId }) => {
      return apiClient.post(`/api/cisa/departments/${departmentId}/capability-answers`, {
        answers,
        workshopSessionId
      });
    },
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: ['cisa', 'capability-answers', departmentId]
      });
      queryClient.invalidateQueries({
        queryKey: ['cisa', 'derived-maturity-capabilities', departmentId]
      });
    }
  });
}

/**
 * Mutation to set/remove maturity override
 */
export function useMaturityOverride(departmentId) {
  const queryClient = useQueryClient();

  const setOverride = useMutation({
    mutationFn: async ({ functionCode, overrideLevel, reason }) => {
      return apiClient.post(`/api/cisa/departments/${departmentId}/maturity-override`, {
        functionCode,
        overrideLevel,
        reason
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['cisa', 'derived-maturity-capabilities', departmentId]
      });
    }
  });

  const removeOverride = useMutation({
    mutationFn: async (functionCode) => {
      return apiClient.del(`/api/cisa/departments/${departmentId}/maturity-override/${functionCode}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['cisa', 'derived-maturity-capabilities', departmentId]
      });
    }
  });

  return { setOverride, removeOverride };
}

/**
 * Combined hook for full capability assessment workflow
 */
export function useCapabilityAssessment(departmentId, functionCode = null) {
  const queryClient = useQueryClient();

  // Fetch questions
  const questionsQuery = useCapabilityQuestions(functionCode);

  // Fetch answers
  const answersQuery = useCapabilityAnswers(departmentId, functionCode);

  // Fetch derived levels
  const derivedQuery = useDerivedMaturityFromCapabilities(departmentId);

  // Save mutation
  const saveMutation = useSaveCapabilityAnswers(departmentId);

  // Override mutation
  const { setOverride, removeOverride } = useMaturityOverride(departmentId);

  // Transform answers into a map for easy lookup
  const answersMap = useMemo(() => {
    const map = {};
    if (answersQuery.data?.answersByFunction) {
      Object.values(answersQuery.data.answersByFunction).flat().forEach(ans => {
        map[ans.questionId] = {
          answer: ans.answer,
          evidenceNotes: ans.evidenceNotes
        };
      });
    }
    return map;
  }, [answersQuery.data]);

  // Flatten questions if grouped
  const questions = useMemo(() => {
    const data = questionsQuery.data;
    if (!data) return [];

    // If it's an array of grouped data
    if (Array.isArray(data)) {
      if (data[0]?.questions) {
        // Grouped by function
        return data.flatMap(group => group.questions.map(q => ({
          ...q,
          functionCode: group.functionCode,
          functionName: group.functionName,
          pillarCode: group.pillarCode
        })));
      }
      return data; // Already flat
    }

    // Single function response
    if (data.questions) {
      return data.questions.map(q => ({
        ...q,
        functionCode: data.functionCode,
        functionName: data.functionName,
        pillarCode: data.pillarCode
      }));
    }

    return [];
  }, [questionsQuery.data]);

  // Calculate derived level for specific function
  const getDerivedLevel = useCallback((fnCode) => {
    return derivedQuery.data?.derivedLevels?.[fnCode] || 'TRADITIONAL';
  }, [derivedQuery.data]);

  // Get function metadata (override info, etc.)
  const getFunctionMeta = useCallback((fnCode) => {
    return derivedQuery.data?.functionMeta?.[fnCode] || {
      derived: 'TRADITIONAL',
      capabilitiesAnswered: 0,
      hasOverride: false
    };
  }, [derivedQuery.data]);

  // Save a single answer
  const saveAnswer = useCallback(async (questionId, answer, evidenceNotes = '') => {
    return saveMutation.mutateAsync({
      answers: [{ questionId, answer, evidenceNotes }]
    });
  }, [saveMutation]);

  // Save multiple answers at once
  const saveAnswers = useCallback(async (answers) => {
    return saveMutation.mutateAsync({ answers });
  }, [saveMutation]);

  // Set override for a function
  const setFunctionOverride = useCallback(async (fnCode, level, reason) => {
    return setOverride.mutateAsync({
      functionCode: fnCode,
      overrideLevel: level,
      reason
    });
  }, [setOverride]);

  // Remove override for a function
  const removeFunctionOverride = useCallback(async (fnCode) => {
    return removeOverride.mutateAsync(fnCode);
  }, [removeOverride]);

  // Calculate missing capabilities for gap analysis
  const getMissingCapabilities = useCallback((fnCode, targetLevel) => {
    const fnQuestions = questions.filter(q => q.functionCode === fnCode);
    const targetIdx = MATURITY_LEVELS.findIndex(m => m.key === targetLevel);

    return fnQuestions
      .filter(q => {
        const levelIdx = MATURITY_LEVELS.findIndex(m => m.key === q.impliesLevel);
        const isInScope = levelIdx <= targetIdx;
        const isNotAchieved = answersMap[q.questionId]?.answer !== true;
        return isInScope && isNotAchieved;
      })
      .map(q => ({
        questionId: q.questionId,
        questionText: q.questionText,
        impliesLevel: q.impliesLevel,
        priority: getPriority(q.impliesLevel, targetLevel)
      }));
  }, [questions, answersMap]);

  return {
    // Data
    questions,
    answersMap,
    derivedLevels: derivedQuery.data?.derivedLevels || {},

    // Loading states
    isLoading: questionsQuery.isLoading || answersQuery.isLoading,
    isLoadingDerived: derivedQuery.isLoading,
    isSaving: saveMutation.isPending,

    // Errors
    error: questionsQuery.error || answersQuery.error || derivedQuery.error,

    // Actions
    saveAnswer,
    saveAnswers,
    setFunctionOverride,
    removeFunctionOverride,

    // Helpers
    getDerivedLevel,
    getFunctionMeta,
    getMissingCapabilities,

    // Refetch
    refetch: () => {
      questionsQuery.refetch();
      answersQuery.refetch();
      derivedQuery.refetch();
    }
  };
}

/**
 * Determine priority based on how far the level is from target
 */
function getPriority(impliesLevel, targetLevel) {
  const impliesIdx = MATURITY_LEVELS.findIndex(m => m.key === impliesLevel);
  const targetIdx = MATURITY_LEVELS.findIndex(m => m.key === targetLevel);

  // Items at or just below target are high priority
  if (impliesIdx >= targetIdx - 1) return 'high';
  // Items at intermediate levels are medium
  if (impliesIdx >= targetIdx - 2) return 'medium';
  // Earlier items are low priority
  return 'low';
}

export default useCapabilityAssessment;
